import { getRealUrlPrefix, TypeBoundAxiosInstance } from '@m43/core';
import { Socket, StreamBroadcaster } from '@m43/sockets-mark43';
import { UserResource } from '@mark43/rms-api';
import { StreamMap } from './stream-types';
import { getAppName } from '../app-name/app-name';
import { logDebug, logError, logInfo, logWarn } from '../monitoring/log';
import { createSpan, startSpan } from '../monitoring/otel-client';

let currentUserId: number | undefined;

export function setSocketUserId(userId: number) {
    currentUserId = userId;
}

export function createSocket(args: {
    apiClient: TypeBoundAxiosInstance;
    streamBroadcaster: StreamBroadcaster<StreamMap>;
}) {
    const { apiClient, streamBroadcaster } = args;
    const appName = getAppName();
    const socket = new Socket({
        socketBaseUrl: `${getRealUrlPrefix().replace('http', 'ws')}/${
            appName === 'cad' ? 'cad' : 'fr'
        }-sync-next/api/entity/`,
        fetchSocketToken: async (): Promise<string> => {
            const response = await apiClient.request<UserResource.GenerateRealtimeToken>({
                method: 'GET',
                url: 'user/realtime',
                service: 'RMS',
            });
            return response.data.data!;
        },
        streamBroadcaster,
        // if the socket has not received a message in this many seconds it shifts into a 'FATAL' status
        messageTimeoutThreshold: 22000,
        messageTimeoutCheckFrequency: 3000,
        onMessage(message, metadata) {
            if (message.streamName === 'gps') return;
            if (
                metadata &&
                metadata.originatingTraceId &&
                currentUserId !== undefined &&
                metadata.originatingUserId === currentUserId
            ) {
                const now = Date.now();
                const sharedAttrs = {
                    'ws.stream_topic': metadata.streamTopic,
                    'ws.stream_name': String(message.streamName),
                    'ws.operation_name': metadata.operationName ?? '',
                };

                // Parent span: full e2e
                const e2eSpan = startSpan({
                    name: 'websocket.e2e',
                    startTime: metadata.requestInitiatedMillis,
                    attributes: {
                        ...sharedAttrs,
                    },
                    links: [
                        {
                            traceId: metadata.originatingTraceId.padStart(32, '0'),
                            spanId: metadata.originatingSpanId.padStart(16, '0'),
                        },
                    ],
                });

                // Child: api processing → redis
                createSpan({
                    name: 'websocket.time_in_api',
                    parentSpan: e2eSpan,
                    startTime: metadata.apiProcessingStartMillis,
                    endTime: metadata.addedToRedisMillis,
                    attributes: sharedAttrs,
                });

                // Child: redis → broadcaster
                createSpan({
                    name: 'websocket.time_in_redis',
                    parentSpan: e2eSpan,
                    startTime: metadata.addedToRedisMillis,
                    endTime: metadata.websocketBroadcastedMillis,
                    attributes: sharedAttrs,
                });

                // Sibling: e2e duration minus time spent in api
                if (metadata.apiProcessingStartMillis && metadata.addedToRedisMillis) {
                    const apiDuration =
                        metadata.addedToRedisMillis - metadata.apiProcessingStartMillis;
                    const e2eWithoutApiDuration =
                        now - metadata.requestInitiatedMillis - apiDuration;

                    if (e2eWithoutApiDuration < 0) {
                        logDebug(
                            'websocket.e2e_without_time_in_api would be negative, skipping span',
                            {
                                type: 'negative-e2e-without-api',
                                traceId: e2eSpan.spanContext().traceId,
                                spanId: e2eSpan.spanContext().spanId,
                                e2eWithoutApiDuration,
                            }
                        );
                    } else {
                        const e2eWithoutApiSpan = startSpan({
                            name: 'websocket.e2e_without_time_in_api',
                            startTime: metadata.requestInitiatedMillis,
                            attributes: {
                                ...sharedAttrs,
                            },
                            links: [
                                {
                                    traceId: e2eSpan.spanContext().traceId,
                                    spanId: e2eSpan.spanContext().spanId,
                                },
                            ],
                        });

                        // Bidirectional link: e2e ↔ e2e_without_time_in_api
                        e2eSpan.addLink({
                            context: e2eWithoutApiSpan.spanContext(),
                        });

                        e2eWithoutApiSpan.end(now - apiDuration);
                    }
                }

                e2eSpan.end(now);
            }
        },
        onSocketStatusChange(status) {
            logInfo(`socket status changed to ${status.status}`);
        },
        onWarning(warning) {
            logWarn(warning);
        },
        onFatal(source, eventData) {
            logError(new Error('socket fatal error'), {
                type: `socket_fatal.${source}`,
                eventData: JSON.stringify(eventData),
            });
        },
        onOutOfOrderMessage({ streamName, messagePosition }) {
            logDebug('Socket received message with unexpected last message position', {
                type: 'out-of-order-socket-message',
                streamName: String(streamName),
                messagePosition,
            });
        },
    });
    socket.setApplicationName(appName);
    return socket;
}
